import { Link } from "react-router-dom";
import { Heart, Sparkles, Users, ShieldCheck } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { HERO_IMG } from "@/lib/data";
import { useSettings } from "@/lib/settings";

const VALUES = [
  { icon: Heart, title: "Family Owned", desc: "Named after our daughter, Briar, who inspires everything we make." },
  { icon: ShieldCheck, title: "Made With Care", desc: "Every project completed with attention to detail, start to finish." },
  { icon: Users, title: "Personalized Service", desc: "We take the time to truly understand your needs and your vision." },
  { icon: Sparkles, title: "Worn With Pride", desc: "High-quality apparel made to be loved, worn, and remembered." },
];

export default function About() {
  const { settings } = useSettings();
  return (
    <div data-testid="about-page">
      <PageHero eyebrow="ABOUT US" title="OUR STORY" script="named after our girl, Briar" />

      {/* Intro */}
      <section className="py-20 bg-paper">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <img src={HERO_IMG} alt="The Briars Unlimited family" className="w-full object-cover object-top aspect-[4/5]" />
              <div className="absolute -bottom-5 -right-5 hidden sm:block bg-ink text-[#f2f0ea] px-7 py-5">
                <div className="font-script text-gold text-4xl leading-none">Briar</div>
                <div className="text-[10px] tracking-[0.28em] mt-1 text-white/70">OUR INSPIRATION</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-script text-gold text-5xl mb-2">Welcome to</p>
            <h2 className="font-display text-4xl sm:text-5xl mb-6 leading-[1.02]">BRIARS UNLIMITED!</h2>
            <div className="space-y-5 text-navy/75 leading-relaxed">
              <p>
                We're a family-owned custom apparel company passionate about helping our customers bring their ideas
                to life. Our business is named after our daughter, <span className="text-navy font-semibold">Briar</span>,
                who inspires us every day. She reminds us why building something meaningful for our family—and yours—is so important.
              </p>
              <p>
                From local businesses and schools to sports teams, equestrian groups, organizations, and special events,
                we create high-quality custom apparel that's made to be worn with pride.
              </p>
            </div>
            <Link to="/quote" className="inline-block mt-8 bg-ink text-[#f2f0ea] px-9 py-4 text-[13px] font-bold tracking-[0.18em] hover:bg-navy transition-colors">
              WORK WITH US
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Story continued */}
      <section className="py-20 bg-[#e9e6dd]">
        <div className="max-w-[900px] mx-auto px-5 lg:px-8 space-y-8">
          <Reveal>
            <p className="text-navy/80 text-lg sm:text-xl leading-relaxed">
              We believe every design tells a story. Whether you're promoting your business, celebrating a milestone,
              supporting your team, or building school spirit, we're committed to delivering products that reflect your
              vision with exceptional quality and attention to detail.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-navy/80 text-lg sm:text-xl leading-relaxed">
              At Briars Unlimited, every order matters. We take the time to understand your needs, provide personalized
              service, and ensure every project is completed with care from start to finish.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-navy/80 text-lg sm:text-xl leading-relaxed">
              Thank you for supporting our family-owned business. We look forward to helping you create apparel that
              people will love to wear and remember.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-paper">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="border-t-2 border-gold pt-6">
                  <v.icon size={30} className="text-navy mb-4" strokeWidth={1.4} />
                  <h3 className="font-display text-lg tracking-[0.05em] mb-2">{v.title.toUpperCase()}</h3>
                  <p className="text-navy/65 text-sm leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Signature */}
      <section className="bg-ink text-[#f2f0ea] py-20">
        <div className="max-w-[900px] mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <div className="font-display text-4xl sm:text-5xl tracking-[0.06em]">BRIARS UNLIMITED</div>
            <p className="text-white/60 max-w-xl mx-auto mt-5 leading-relaxed">
              Creating custom apparel that brings businesses, schools, teams, and communities together.
            </p>
            <div className="w-16 h-1 bg-gold mx-auto mt-8" />
            <p className="text-white/50 text-sm mt-8">{settings.location} · {settings.phone} · {settings.email}</p>
            <Link to="/quote" className="inline-block mt-8 bg-[#f2f0ea] text-ink px-9 py-4 text-[13px] font-bold tracking-[0.18em] hover:bg-gold transition-colors">
              GET A QUOTE
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
